import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";

import { inbox } from "./inbox.graphql";
import type { InboxCategory, InboxCategoryCount } from "./kinds";
import {
  inboxQueryVariables,
  inboxRawPage,
  INBOX_PAGE_SIZE,
  type InboxQueryVariables,
} from "./query";
import { parseInboxRow, type InboxItem } from "./registry";
import type { InboxRow } from "./row";

type InboxQuery = {
  inbox?: {
    total?: number | null;
    counts?: InboxCategoryCount[] | null;
    items?: InboxRow[] | null;
  } | null;
};

export { INBOX_PAGE_SIZE, inboxQueryVariables };
export type { InboxQueryVariables };

export const useInboxItems = (
  enabled: boolean,
  options?: { category?: InboxCategory; pageSize?: number }
): {
  items: InboxItem[];
  total: number;
  counts: InboxCategoryCount[];
  waitingCount: number;
  loading: boolean;
  error: unknown;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
} => {
  const category = options?.category ?? "EVERYTHING";
  const pageSize = options?.pageSize;
  const variables = inboxQueryVariables({
    category,
    ...(pageSize != null ? { limit: pageSize } : {}),
  });
  const { data, loading, error, fetchMore } = useQuery<
    InboxQuery,
    InboxQueryVariables
  >(inbox, {
    skip: !enabled,
    variables,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const rawItems = data?.inbox?.items ?? [];
  const items = useMemo(
    () =>
      (data?.inbox?.items ?? []).flatMap((row) => {
        const item = parseInboxRow(row);
        return item ? [item] : [];
      }),
    [data]
  );
  const total = data?.inbox?.total ?? 0;
  const counts = data?.inbox?.counts ?? [];
  const { hasMore, after } = inboxRawPage(rawItems, total, pageSize);
  const loadMore = () => {
    if (!hasMore || !after || loadingMore) {
      return;
    }
    setLoadingMore(true);
    void fetchMore({
      variables: inboxQueryVariables({
        category,
        after,
        ...(pageSize != null ? { limit: pageSize } : {}),
      }),
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.inbox) {
          return previous;
        }
        return {
          inbox: {
            ...fetchMoreResult.inbox,
            items: [
              ...(previous.inbox?.items ?? []),
              ...(fetchMoreResult.inbox.items ?? []),
            ],
          },
        };
      },
    }).finally(() => {
      setLoadingMore(false);
    });
  };
  return {
    items,
    total,
    counts,
    waitingCount: total,
    loading,
    error,
    hasMore,
    loadingMore,
    loadMore,
  };
};
